import React, { useMemo } from 'react'

import { IDashboardExample } from 'types/GlobalDashboardExample'

import { useAccountSettings } from 'redux/hooks/SignedInAccount'
import {
  useDashboardExamples,
  useDashboardExamplesFetchStatus
} from 'redux/hooks/DashboardExamples'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'language/translator'

import Box from 'components_new/atoms/Box'
import { ChipProps } from 'components_new/atoms/Chip'
import Text from 'components_new/atoms/Text'

import GenericThumbnail from 'components_new/molecules/GenericThumbnail'
import ThumbnailList from 'components_new/molecules/ThumbnailList'

import { sortAlphabeticalAsc } from 'utils/sortHelper'
import { getDashboardImageUrl, getThumbnail } from 'utils/functions'

import Loading from './loading'
import Icon from 'components_new/atoms/Icon'

function getExampleChips(
  dashboard: IDashboardExample,
  t: ReturnType<typeof useTranslation>['t']
): ChipProps[] {
  const createdDayDiff = Math.round(
    (Date.now() - Date.parse(dashboard.created_at)) / (1000 * 3600 * 24)
  )
  const updatedDayDiff = Math.round(
    (Date.now() - Date.parse(dashboard.updated_at)) / (1000 * 3600 * 24)
  )
  const showNew = createdDayDiff < 30
  const showUpdated = updatedDayDiff < 14 && !showNew

  if (showNew) return [{ color: 'brand', label: `${t('global', 'new')} ✨` }]
  if (showUpdated)
    return [{ color: 'brand', label: `${t('global', 'updated')} 🎉` }]

  return []
}

interface DashboardExampleExplorerProps {
  category: string | null
  search: string | null
  segment: string | null
  sx?: object
}

const DashboardExampleExplorer = (props: DashboardExampleExplorerProps) => {
  const { category, search, segment, sx } = props

  const history = useHistory()
  const { isHomepal } = useAccountSettings()
  const { t, lang } = useTranslation()

  const dashboardExamples = useDashboardExamples()
  const fetchStatus = useDashboardExamplesFetchStatus()

  const filteredDashboards = useMemo(() => {
    const dashboards = Object.values(dashboardExamples)
      // exclude hidden and sort by title asc
      .filter((dashboard) => !dashboard.hidden)
      .sort((a, b) => sortAlphabeticalAsc(a.title[lang], b.title[lang]))

    const filtered =
      category || segment
        ? dashboards.filter((dashboard) => {
          // filter by category
          const c = category
            ? category.includes(dashboard.category.slug)
            : true

          // filter by segment
          const s = segment
            ? dashboard.segments.some((item) => item.slug === segment)
            : true

          return c && s
        })
        : dashboards

    const term = search?.toLowerCase().trim()

    if (!term) return filtered

    return filtered.filter((dashboard) =>
      dashboard.title[lang].toLowerCase().includes(term)
    )
  }, [dashboardExamples, category, segment, search, lang])

  // loading
  if (fetchStatus !== 'success') return <Loading />

  // success
  return (
    <Box sx={sx}>
      <Text component="p" variant="caption">
        {filteredDashboards.length} {t('global', 'dashboards', true)}
      </Text>

      <Box>
        <ThumbnailList
          columns={12}
          itemSize={{ xs: 12, sm: 6, md: 6, lg: 4, xl: 3 }}
        >
          {filteredDashboards.map((dashboard) => (
            <GenericThumbnail
              body={
                dashboard.author.name
                  ? dashboard.author.company
                    ? `${dashboard.author.name} · ${dashboard.author.company}`
                    : dashboard.author.name
                  : 'Homepal'
              }
              chips={getExampleChips(dashboard, t)}
              content={
                dashboard.data.visits.total && isHomepal ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Icon
                      name="VisibilityOutlined"
                      sx={{ color: 'text.secondary', fontSize: 14 }}
                    />
                    <Text color="text.secondary" variant="caption">
                      {dashboard.data.visits.total}
                    </Text>
                  </Box>
                ) : null
              }
              href={`/templates/${dashboard.id}`}
              key={dashboard.id}
              media={{ src: getThumbnail(getDashboardImageUrl(dashboard.id)) }}
              search={history.location.search}
              title={dashboard.title[lang]}
              variant={dashboard.author.name ? 'user' : 'homepal'}
            />
          ))}
        </ThumbnailList>
      </Box>
    </Box>
  )
}

export default DashboardExampleExplorer
